import MeritoButton from "../../../components/common/MeritoButtons/MeritoButtons";
import styles from "./Checklist.module.scss";
import { IoMdAlert as AlertIcon } from "react-icons/io";
import { RiCheckboxCircleFill as SuccessIcon } from "react-icons/ri";
import { HiChevronRight as RightArrowIcon } from "react-icons/hi";
import {
  UnderwritingFormContext,
  UnderwritingFormContextType,
} from "../context/UnderwritingFormContext";
import { Fragment, useContext, useEffect, useMemo, useState } from "react";
import {
  ApplicationContextType,
  IIntegrationPartner,
  IpNames,
  LendingStatus,
  monthlyReportRoutes,
  MonthlyReportSteps,
  UnderwritingSteps,
} from "../../../interfaces/interfaces";
import { RestS } from "../../../rest/rest-service";
import { IpRestS } from "../../../rest/ipRest-service";
import {
  getAccountConnectionInfo,
  getBankStatementErrorInfo,
  getBusinessUpdatesErrorInfo,
  getCompanyDetailsErrorInfo,
  getGstStepErrorInfo,
  getInvestmentDetailsErrorInfo,
  getRevenueDetailsErrorInfo,
  getSalesMarketingErrorInfo,
} from "./checklistHelper";
import CircularLoader from "../../../components/common/CircularLoader/CircularLoader";
import { useNavigate } from "react-router";
import UnderwritingDeclaration from "../UnderwritingDeclaration/UnderwritingDeclaration";
import { MonthlyReportFormContext } from "../../MonthlyReport/context/MonthlyReportFormContext";
import ModalDialog from "../../../components/common/Modal";
import { BsFillExclamationCircleFill } from "react-icons/bs";
import { validateGstDetails } from "../CompanyDetails/companyDetailsHelper";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesomeOutlined";
import { fetchSuggestedIps } from "../integrationSuggestions";
import PendingChannelsPopup from "../PendingChannelsPopup/PendingChannelsPopup";
import { useAppDispatch, useAppSelector } from "../../../redux/reduxHooks";
import { setRefreshIntegrationSuggestions } from "../../../redux/underwriting/underwriting";
import {
  toChecklistValidation,
  useOptionalBankStatements,
} from "../../../components/BankStatements";

type Props = {
  stepperList: any[];
  monthYear?: string;
  formType?: ApplicationContextType;
};

const ChecklistPage = ({
  stepperList,
  monthYear = null,
  formType = ApplicationContextType.underwriting,
}: Props) => {
  const [loading, setLoading] = useState(true);
  const pulseBankStatements = useOptionalBankStatements();

  const formContext = useContext(
    formType == ApplicationContextType.monthlyReport
      ? MonthlyReportFormContext
      : UnderwritingFormContext
  ) as UnderwritingFormContextType;
  const {
    formData,
    underwritingFileJobs,
    statementAnalysisJobs,
    extraFormDetails,
    gstAnalysisJobs,
    revenueClassificationJobs,
    // Underwriting only — the monthly report form has no MIS card.
    misClassificationJob,
  } = formContext;
  // only underwriting has a debt step
  const serverDebts = "serverDebts" in formContext ? formContext.serverDebts : null;
  // const [isSalesConnected, setIsSalesConnected] = useState(false);
  const [accountsData, setAccountsData] = useState<any[]>([]);
  const navigate = useNavigate();
  const [submitLoading, setSubmitLoading] = useState(false);
  const [isSubmitReportOpen, setIsSubmitReportOpen] = useState(false);

  const formatIpObj = (ipArr: IIntegrationPartner[]) => {
    return ipArr.reduce((prev, curVal) => {
      prev[curVal?.companyName] = {
        ipName: curVal?.companyName,
        isSalesChannel: curVal?.isSalesChannel,
        ...curVal,
      };
      return prev;
    }, {});
  };

  const [isDeclarationOpen, setIsDeclarationOpen] = useState(false);
  const [showPendingChannelsPopup, setShowPendingChannelsPopup] =
    useState(false);
  const { refreshIntegrationSuggestions } = useAppSelector(
    (state) => state.underwriting
  );
  const dispatch = useAppDispatch();

  const fetchAccounts = async () => {
    try {
      const allIps = [
        IpNames.shopify,
        IpNames.metaAds,
        IpNames.googleAds,
        IpNames.googleAnalytics,
        IpNames.amazonSellerCentral,
        IpNames.amazonVendorCentral,
        IpNames.amazonAds,
      ];
      const adsChannels = new Set([
        IpNames.metaAds,
        IpNames.googleAds,
        IpNames.amazonAds,
      ]);
      const [{ accounts }, activeIpWorkspaces, suggestedIps] =
        await Promise.all([
          RestS.fetchAccessTokens(),
          IpRestS.getAllActiveWorkspaces(),
          fetchSuggestedIps(),
        ]);
      const groupedAcconts = accounts.reduce((prev, curVal) => {
        prev[curVal.ipName] = curVal;
        return prev;
      }, {});
      const ipMapping = activeIpWorkspaces.reduce((prev, curVal) => {
        if (curVal?.integrationPartner?.combined) {
          prev = {
            ...prev,
            ...formatIpObj(curVal?.integrationPartner?.optionalIp),
            ...formatIpObj(curVal?.integrationPartner?.mandatoryIp),
            ...formatIpObj(curVal?.integrationPartner?.nonMandatoryIp),
          };
        } else {
          prev = {
            ...prev,
            ...formatIpObj([curVal?.integrationPartner]),
          };
        }
        return prev;
      }, {});

      const sortedIps = [...new Set([...allIps, ...Object.keys(ipMapping)])];

      const allAccountsData = sortedIps.reduce((prev, curVal) => {
        const accountData = groupedAcconts[curVal] ?? {
          ipName: curVal,
          isConnected: false,
          accounts: [],
        };
        prev.push({
          ...accountData,
          isSalesChannel: ipMapping[curVal]?.isSalesChannel,
          isAdsChannel: adsChannels.has(curVal as IpNames),
          isSuggested: suggestedIps.has(curVal),
        });
        return prev;
      }, []);
      // setIpMappingData(ipMapping);
      setAccountsData(allAccountsData);
    } catch (error) {
      console.log("error", error);
    }
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      await fetchAccounts();
      setLoading(false);
    })();
  }, []);

  // the discovery scan finishes well after the page loads
  useEffect(() => {
    if (!refreshIntegrationSuggestions) return;
    fetchAccounts();
    dispatch(setRefreshIntegrationSuggestions(false));
  }, [refreshIntegrationSuggestions]);

  const overallErrors = useMemo(() => {
    // console.timeLog('accounts data', accountsData);

    if (formType == ApplicationContextType.monthlyReport) {
      const accountsValidation = getAccountConnectionInfo(accountsData);
      const bankStatementValidation = getBankStatementErrorInfo(
        formData,
        underwritingFileJobs,
        statementAnalysisJobs,
        ApplicationContextType.monthlyReport
      );

      const salesMarketingValidation = getSalesMarketingErrorInfo(formData);

      const businessUpdatesValidation = getBusinessUpdatesErrorInfo(
        formData,
        monthYear
      );

      const gstStepValidation = getGstStepErrorInfo(
        formData,
        underwritingFileJobs,
        gstAnalysisJobs,
        formType
      );

      return {
        [MonthlyReportSteps.connectAccounts]: accountsValidation,
        [MonthlyReportSteps.bankStatement]: bankStatementValidation,
        [MonthlyReportSteps.salesMarketing]: salesMarketingValidation,
        [MonthlyReportSteps.businessUpdates]: businessUpdatesValidation,
        [MonthlyReportSteps.gst]: gstStepValidation,
      };
    } else {
      const accountsValiation = getAccountConnectionInfo(
        accountsData,
        Boolean(formData?.integrationCheckOverride)
      );

      const pulseStatus = pulseBankStatements?.checklistStatus;
      const bankStatementValidation = pulseStatus
        ? toChecklistValidation(pulseStatus)
        : getBankStatementErrorInfo(
            formData,
            underwritingFileJobs,
            statementAnalysisJobs
          );
      // console.log('bankStatementValidation', bankStatementValidation);
      const companyStepValidation = getCompanyDetailsErrorInfo(
        formData,
        underwritingFileJobs,
        gstAnalysisJobs,
        formType
      );
      console.log("company validation error", companyStepValidation);
      const revenueStepValidation = getRevenueDetailsErrorInfo(
        formData,
        underwritingFileJobs,
        revenueClassificationJobs,
        serverDebts,
        misClassificationJob
      );
      const investmentsStepValidation = getInvestmentDetailsErrorInfo(formData);

      return {
        [UnderwritingSteps.accounts]: accountsValiation,
        [UnderwritingSteps.documents]: bankStatementValidation,
        [UnderwritingSteps.company]: companyStepValidation,
        [UnderwritingSteps.revenue]: revenueStepValidation,
        [UnderwritingSteps.investments]: investmentsStepValidation,
      };
    }
  }, [
    formData,
    formType,
    underwritingFileJobs,
    statementAnalysisJobs,
    serverDebts,
    accountsData,
    pulseBankStatements?.checklistStatus,
    // Both arrive after mount (fetch, then socket pushes), so without them a
    // classification failure never reaches the checklist.
    revenueClassificationJobs,
    misClassificationJob,
  ]);

  // at least one channel disconnected, or suggested but never connected
  const hasPendingChannels = useMemo(() => {
    const accountsStatus: any = overallErrors[UnderwritingSteps.accounts];
    return Boolean(
      accountsStatus?.disconnectedCount || accountsStatus?.pendingCount
    );
  }, [overallErrors]);

  const openDeclaration = () => {
    setShowPendingChannelsPopup(false);
    setIsDeclarationOpen(true);
  };

  const isSubmitDisabled = useMemo(() => {
    console.log("overall error in submit", overallErrors);
    return Object.values(overallErrors).some((item: any) => {
      if (item?.isLoading) return true;
      else if (item?.isError) {
        console.log("submit disable", item);
        console.log("submit disable 2", item?.isError);
        return true;
      }
      return false;
    });
  }, [overallErrors]);

  // console.log('overallErrors', overallErrors);

  const handleFinalFormSubmit = (declarationValues) => {
    let submitFormData = {
      ...extraFormDetails,
      data: {
        ...formData,
        ...declarationValues,
      },
      applicationStatus: LendingStatus.PENDING_APPROVAL,
    };
    onFinalSubmitForm(submitFormData, true);
  };

  const onFinalSubmitForm = async (submitFormData, isLastStep = false) => {
    try {
      if (isLastStep) setSubmitLoading(true);
      // setFormData(submitFormData?.formData);
      // await delaySubmitting()
      const data: any = await RestS.submitUnderwritingFormData(submitFormData);
      // handleAndSetFormStatuses(data?.formStatuses)
      // setFormData(data?.formData['data']);
      // if (isLastStep) {
      console.log("coming in last step");
      navigate("/underwriting/status");
      // }
    } catch (error) {
      console.log("error", error);
    } finally {
      setSubmitLoading(false);
      setIsDeclarationOpen(false);
    }
  };

  const handleMonthlyReportSubmit = async () => {
    try {
      setSubmitLoading(true);

      console.log("extraFormDetails", extraFormDetails);
      // console.log('monthYear', monthYear);
      // return;
      let submitFormData = {
        ...extraFormDetails,
        data: {
          ...formData,
        },
        applicationStatus: LendingStatus.COMPLETED,
        monthYear: monthYear,
      };

      const data: any = await RestS.submitMonthlyReportFormData(submitFormData);

      if (data?.success) {
        navigate(monthlyReportRoutes.monthlyReportHome);
      }
    } catch (error) {
      console.log("error", error);
    } finally {
      setSubmitLoading(false);
      setIsSubmitReportOpen(false);
    }
  };

  const loadingEnabledSteps = new Set([
    UnderwritingSteps.documents,
    UnderwritingSteps.company,
    UnderwritingSteps.revenue,
    MonthlyReportSteps.gst,
    MonthlyReportSteps.bankStatement,
  ]);

  return (
    <div className={styles.rootContainer}>
      {loading ? (
        <div className={styles.loaderContainer}>
          <CircularLoader />
        </div>
      ) : (
        <>
          <div className={styles.checklistRootContainer}>
            <div className={styles.titleContainer}>
              <div className={styles.title}>Progress Checklist</div>
              <div className={styles.subTitle}>
                <>
                  Complete all the steps below to finish your{" "}
                  {formType == ApplicationContextType.monthlyReport
                    ? "monthly report"
                    : "application"}
                </>
              </div>
            </div>

            <div className={styles.checklistContainer}>
              {stepperList.map((item, index) => {
                const overallObj = overallErrors[item.name];
                console.log("overall errors", overallObj);
                const isError = overallErrors[item.name]?.isError;
                const overviewMessage = isError
                  ? overallErrors[item.name]?.errorDetails?.message
                  : overallErrors[item.name]?.successDetails?.message;
                const overviewSegments = isError
                  ? overallObj?.errorDetails?.segments
                  : overallObj?.successDetails?.segments;
                return (
                  <div
                    className={styles.checklistItemContainer}
                    onClick={() => {
                      if (isError) {
                        navigate(`${item.link}?error=true`);
                      } else {
                        navigate(item.link);
                      }
                    }}
                  >
                    <div className={styles.leftContainer}>
                      <div
                        className={`${styles.iconContainer} ${
                          isError ? styles.error : styles.success
                        }`}
                      >
                        {loadingEnabledSteps.has(item?.name) &&
                        overallObj?.isLoading ? (
                          <CircularLoader />
                        ) : isError ? (
                          <AlertIcon />
                        ) : (
                          <SuccessIcon />
                        )}
                      </div>
                      <div className={styles.listTitleContainer}>
                        <div className={styles.title}>{item.title}</div>
                        <div className={styles.overviewContainer}>
                          {overviewSegments?.length
                            ? overviewSegments.map((segment, segmentIndex) => (
                                <Fragment key={segment.label}>
                                  {segmentIndex ? (
                                    <span className={styles.separatorDot} />
                                  ) : null}
                                  <span
                                    className={`${styles.overviewSegment} ${
                                      styles[segment.tone]
                                    }`}
                                  >
                                    {segment.tone === "pending" ? (
                                      <AutoAwesomeIcon />
                                    ) : null}
                                    {segment.label}
                                  </span>
                                </Fragment>
                              ))
                            : overviewMessage}
                        </div>
                      </div>
                    </div>
                    <div className={styles.rightContainer}>
                      <div className={styles.btnContainer}>
                        <MeritoButton
                          onlyIcon={true}
                          icon={<RightArrowIcon />}
                          type="tertiary"
                          size="md2"
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className={styles.bottomBtnContainer}>
              <MeritoButton
                disabled={isSubmitDisabled}
                type="primary-1"
                btnText={
                  formType == ApplicationContextType.monthlyReport
                    ? "Submit Report"
                    : "Submit"
                }
                icon={<RightArrowIcon />}
                onClick={() => {
                  if (formType == ApplicationContextType.monthlyReport) {
                    setIsSubmitReportOpen(true);
                  } else if (hasPendingChannels) {
                    setShowPendingChannelsPopup(true);
                  } else {
                    setIsDeclarationOpen(true);
                  }
                }}
                size="md2"
                isIconToRight={true}
              />
            </div>
          </div>
          <PendingChannelsPopup
            visible={showPendingChannelsPopup}
            toggle={() => setShowPendingChannelsPopup(false)}
            proceedBtnText="Submit Anyway"
            onProceed={openDeclaration}
            onConnect={() => {
              setShowPendingChannelsPopup(false);
              const accountsStep = stepperList.find(
                (item) => item.name == UnderwritingSteps.accounts
              );
              if (accountsStep?.link) navigate(accountsStep.link);
            }}
          />

          <UnderwritingDeclaration
            isSubmitLoading={submitLoading}
            handleClose={() => setIsDeclarationOpen(false)}
            isOpen={isDeclarationOpen}
            handleSubmit={(values) => handleFinalFormSubmit(values)}
          />

          <ModalDialog
            visible={isSubmitReportOpen}
            toggle={(prop) => {
              setIsSubmitReportOpen(!prop);
            }}
            size="md"
            roundedBorder={true}
          >
            <>
              <div className={styles.submitReport}>
                <div className={styles.body}>
                  <BsFillExclamationCircleFill />
                  <h2>Submit Report?</h2>
                  <p>Your responses cannot be edited once submitted.</p>
                </div>
                <div className={styles.footer}>
                  <MeritoButton
                    btnText="Review"
                    type="secondary-2"
                    size="lg"
                    isRounded={true}
                    onClick={() => setIsSubmitReportOpen(false)}
                  />
                  <MeritoButton
                    btnText="Submit"
                    loading={submitLoading}
                    type="primary-1"
                    size="lg"
                    isRounded={true}
                    onClick={() => {
                      // Submit Report
                      handleMonthlyReportSubmit();
                    }}
                  />
                </div>
              </div>
            </>
          </ModalDialog>
        </>
      )}
    </div>
  );
};

export default ChecklistPage;
